/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createOnCallSchedule } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type OnCallScheduleDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function OnCallScheduleDialog({ actionDialogReturn, onNavigate: _onNavigate }: OnCallScheduleDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { setWorkspaceVersion } = useWorkspace();
  const { toast } = useFeedback();

  if (!actionDialog || actionDialog.kind !== 'oncall-schedule') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const canSubmit =
    actionDialog.name.trim().length >= 2 &&
    actionDialog.teamHandle.trim().length >= 1 &&
    actionDialog.timezone.trim().length >= 1;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'oncall-schedule' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      await createOnCallSchedule(session.accessToken, {
        organizationId: actionDialog.organizationId,
        teamHandle: actionDialog.teamHandle.trim(),
        name: actionDialog.name.trim(),
        rotationType: actionDialog.rotationType,
        timezone: actionDialog.timezone.trim()
      });
      setWorkspaceVersion((v) => v + 1);
      toast('success', `Schedule "${actionDialog.name.trim()}" created`);
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">On-call</div>
            <h3>New on-call schedule</h3>
            <p>Define a rotation for a team so escalations know who is on point.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Name</span>
            <input
              value={actionDialog.name}
              onChange={(e) => setActionDialog({ ...actionDialog, name: e.target.value })}
              placeholder="Platform primary"
              minLength={2}
              autoFocus
            />
          </label>
          <label className="settingsField">
            <span>Team handle</span>
            <input
              value={actionDialog.teamHandle}
              onChange={(e) => setActionDialog({ ...actionDialog, teamHandle: e.target.value })}
              placeholder="platform-eng"
            />
            <p className="fieldHint">The team this schedule provides coverage for.</p>
          </label>
          <label className="settingsField">
            <span>Rotation type</span>
            <select
              value={actionDialog.rotationType}
              onChange={(e) => setActionDialog({ ...actionDialog, rotationType: e.target.value as typeof actionDialog.rotationType })}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <label className="settingsField">
            <span>Timezone</span>
            <input
              value={actionDialog.timezone}
              onChange={(e) => setActionDialog({ ...actionDialog, timezone: e.target.value })}
              placeholder="America/New_York"
            />
            <p className="fieldHint">IANA timezone name used to interpret shift boundaries.</p>
          </label>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy || !canSubmit}>
            {dialogBusy ? 'Creating…' : 'Create schedule'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { OnCallScheduleDialog };
